import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Header from '@/components/ui/Header';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import RequestCard from './components/RequestCard';
import CalendarView from './components/CalendarView';
import FilterControls from './components/FilterControls';
import RequestModal from './components/RequestModal';
import CreateRequestModal from './components/CreateRequestModal';
import ManagerCreateAbsenceModal from './components/ManagerCreateAbsenceModal';
import EmployeeRequestsView from './components/EmployeeRequestsView';
import { useAuth } from '@/context/AuthContext';
import LoadingState from '@/components/ui/LoadingState';
import { cn } from '@/utils/cn';
import {
  fetchEmployees as fetchEmployeesFromApi,
  fetchCurrentEmployee,
} from '@/lib/api/employees';
import {
  fetchTimeOffRequests,
  fetchMyTimeOffRequests,
  createTimeOffRequest,
  updateTimeOffStatus,
} from '@/lib/api/timeOff';
import useTranslation from '@/hooks/useTranslation';
import useNotifications from '@/hooks/useNotifications';
import { NotificationSection } from 'shared';

const preferNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return '';
};

const INACTIVE_ABSENCE_STATUSES = new Set(['denied', 'cancelled', 'canceled']);

const isActiveAbsenceStatus = (status) => {
  if (!status) {
    return true;
  }
  const normalizedStatus = status.toString().toLowerCase();
  return !INACTIVE_ABSENCE_STATUSES.has(normalizedStatus);
};

const DAY_PART_TIME_RANGES = {
  'full-day': { startHour: 0, startMinute: 0, endHour: 23, endMinute: 59 },
  forenoon: { startHour: 0, startMinute: 0, endHour: 11, endMinute: 59 },
  afternoon: { startHour: 12, startMinute: 0, endHour: 23, endMinute: 59 },
};

const parseDateParts = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const segments = value.split('-');
  if (segments.length !== 3) {
    return null;
  }
  const [year, month, day] = segments.map((segment) => Number.parseInt(segment, 10));
  if ([year, month, day].some((part) => Number.isNaN(part))) {
    return null;
  }
  return { year, month, day };
};

const toUtcTimestamp = (parts, hour = 0, minute = 0, second = 0, millisecond = 0) =>
  Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute, second, millisecond);

const computeAbsenceRange = (startDate, endDate, dayPart = 'full-day') => {
  const startParts = parseDateParts(startDate);
  const endParts = parseDateParts(endDate);
  if (!startParts || !endParts) {
    return null;
  }

  const sameDay =
    startParts.year === endParts.year
    && startParts.month === endParts.month
    && startParts.day === endParts.day;

  const normalizedDayPart = sameDay && typeof dayPart === 'string'
    ? dayPart.toLowerCase()
    : 'full-day';

  const range = DAY_PART_TIME_RANGES[normalizedDayPart] ?? DAY_PART_TIME_RANGES['full-day'];

  const start = toUtcTimestamp(startParts, range.startHour, range.startMinute, 0, 0);
  const end = sameDay
    ? toUtcTimestamp(endParts, range.endHour, range.endMinute, 59, 999)
    : toUtcTimestamp(endParts, 23, 59, 59, 999);

  return { start, end };
};

const rangesOverlap = (firstRange, secondRange) => {
  if (!firstRange || !secondRange) {
    return false;
  }
  return firstRange.start <= secondRange.end && secondRange.start <= firstRange.end;
};

const TimeOffManagement = () => {
  const { t, language } = useTranslation(['timeOffManagement', 'common']);
  const translate = useCallback(
    (key, fallback) => t(`timeOffManagement:${key}`, fallback),
    [t],
  );
  const locale = language || 'en-US';
  const [activeView, setActiveView] = useState('requests');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    startDate: '',
    endDate: ''
  });
  const { user, loading: authLoading } = useAuth();
  const resolvedUserRole = user?.role ?? null;
  const userRole = resolvedUserRole ?? 'manager';
  const normalizedUserEmail = user?.email?.toLowerCase?.();
  const isEmployeeView = resolvedUserRole === 'employee';
  const {
    notifications: userNotifications,
    markAsSeen: markNotificationAsSeen,
    ready: notificationsReady,
  } = useNotifications(10000);
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reloadData = useCallback(async () => {
    if (authLoading || !resolvedUserRole) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (isEmployeeView) {
        const [requestData, currentEmployee] = await Promise.all([
          fetchMyTimeOffRequests(),
          fetchCurrentEmployee(),
        ]);
        setRequests(requestData);
        setEmployees(currentEmployee ? [currentEmployee] : []);
      } else {
        const [requestData, employeeData, currentEmployee] = await Promise.all([
          fetchTimeOffRequests(),
          fetchEmployeesFromApi(),
          fetchCurrentEmployee().catch(() => null),
        ]);
        const mergedEmployees = Array.isArray(employeeData) ? [...employeeData] : [];
        if (currentEmployee) {
          const currentIdentifiers = new Set(
            [
              currentEmployee?.id,
              currentEmployee?.employeeId,
              currentEmployee?.raw?.id,
              currentEmployee?.raw?._id,
              currentEmployee?.raw?.employeeId,
              currentEmployee?.email?.toLowerCase?.(),
              currentEmployee?.accountEmail?.toLowerCase?.(),
            ]
              .map((value) => (value ? String(value).toLowerCase() : null))
              .filter(Boolean),
          );

          const existingIndex = mergedEmployees.findIndex((employee) => {
            const candidateIdentifiers = [
              employee?.id,
              employee?.employeeId,
              employee?.raw?.id,
              employee?.raw?._id,
              employee?.raw?.employeeId,
              employee?.email?.toLowerCase?.(),
              employee?.accountEmail?.toLowerCase?.(),
            ]
              .map((value) => (value ? String(value).toLowerCase() : null))
              .filter(Boolean);

            return candidateIdentifiers.some((identifier) => currentIdentifiers.has(identifier));
          });

          if (existingIndex >= 0) {
            mergedEmployees[existingIndex] = {
              ...mergedEmployees[existingIndex],
              ...currentEmployee,
              raw: currentEmployee?.raw ?? mergedEmployees[existingIndex]?.raw,
            };
          } else {
            mergedEmployees.push(currentEmployee);
          }
        }
        setRequests(requestData);
        setEmployees(mergedEmployees);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isEmployeeView, resolvedUserRole]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  useEffect(() => {
    if (!isEmployeeView || !notificationsReady) {
      return;
    }

    const actionableNotifications = (userNotifications ?? []).filter((notification) => {
      if (!notification || notification.seen) {
        return false;
      }

      const sectionMatches =
        notification.section === NotificationSection.ABSENCE_MANAGEMENT;
      if (!sectionMatches) {
        return false;
      }

      const status = notification?.metadata?.status ?? notification?.status;
      const normalizedStatus =
        typeof status === 'string' ? status.toLowerCase() : '';

      return ['approved', 'denied', 'declined', 'accepted'].includes(normalizedStatus);
    });

    if (actionableNotifications.length === 0) {
      return;
    }

    actionableNotifications.forEach((notification) => {
      if (notification?.id) {
        markNotificationAsSeen(notification.id);
      }
    });
  }, [
    isEmployeeView,
    markNotificationAsSeen,
    notificationsReady,
    userNotifications,
  ]);

  // Build quick lookup maps so we can enrich absence requests with the latest employee profile data.
  const employeeLookup = useMemo(() => {
    const byId = new Map();
    const byEmail = new Map();

    if (Array.isArray(employees)) {
      employees.forEach((employee) => {
        if (!employee) {
          return;
        }

        const candidateIds = [
          employee?.id,
          employee?.employeeId,
          employee?.raw?.id,
          employee?.raw?._id,
          employee?.raw?.employeeId,
        ];

        candidateIds.forEach((id) => {
          if (!id) {
            return;
          }
          byId.set(String(id), employee);
        });

        const normalizedEmail = employee?.email?.toLowerCase?.();
        if (normalizedEmail) {
          byEmail.set(normalizedEmail, employee);
        }

        const normalizedAccountEmail = employee?.accountEmail?.toLowerCase?.();
        if (normalizedAccountEmail) {
          byEmail.set(normalizedAccountEmail, employee);
        }
      });
    }

    return { byId, byEmail };
  }, [employees]);

  // Reuse employee directory profile data (avatars, status, etc.) for each absence request.
  const resolvedRequests = useMemo(() => {
    if (!Array.isArray(requests)) {
      return [];
    }

    return requests.map((request) => {
      if (!request?.employee) {
        return request;
      }

      const candidateIds = [
        request.employeeId,
        request.employee?.id,
        request.employee?.employeeId,
        request.employee?.raw?.id,
        request.employee?.raw?._id,
        request.employee?.raw?.employeeId,
      ];

      let matchedEmployee = null;

      for (const candidate of candidateIds) {
        if (!candidate) {
          continue;
        }
        const match = employeeLookup.byId.get(String(candidate));
        if (match) {
          matchedEmployee = match;
          break;
        }
      }

      if (!matchedEmployee) {
        const accountEmail = request.employee?.accountEmail?.toLowerCase?.();
        const email = request.employee?.email?.toLowerCase?.();
        matchedEmployee =
          (accountEmail && employeeLookup.byEmail.get(accountEmail))
          ?? (email && employeeLookup.byEmail.get(email))
          ?? null;
      }

      const fallbackName = request.employee?.name ?? matchedEmployee?.name ?? 'Employee';

      if (!matchedEmployee) {
        return {
          ...request,
          employee: {
            ...request.employee,
            avatarFileId:
              preferNonEmptyString(
                request.employee?.avatarFileId,
                request.employee?.raw?.avatarFileId,
              ) || null,
            avatarUrl:
              preferNonEmptyString(
                request.employee?.avatarUrl,
                request.employee?.raw?.avatarUrl,
              ) || null,
            status: request.employee?.status ?? null,
          },
        };
      }

      const resolvedName = request.employee?.name ?? matchedEmployee?.name ?? fallbackName;
      const resolvedPosition =
        request.employee?.position
        ?? matchedEmployee?.role
        ?? matchedEmployee?.position
        ?? '';
      const resolvedDepartment =
        request.employee?.department ?? matchedEmployee?.department ?? '';
      const resolvedLocation =
        request.employee?.location ?? matchedEmployee?.location ?? undefined;
      const resolvedAvatarFileId = preferNonEmptyString(
        request.employee?.avatarFileId,
        matchedEmployee?.avatarFileId,
        matchedEmployee?.raw?.avatarFileId,
      );
      const resolvedAvatarUrl = preferNonEmptyString(
        request.employee?.avatarUrl,
        matchedEmployee?.avatarUrl,
        matchedEmployee?.raw?.avatarUrl,
        matchedEmployee?.avatar,
      );

      return {
        ...request,
        employee: {
          ...request.employee,
          name: resolvedName,
          position: resolvedPosition,
          department: resolvedDepartment,
          location: resolvedLocation,
          avatarFileId: resolvedAvatarFileId || null,
          avatarUrl: resolvedAvatarUrl || null,
          status: request.employee?.status ?? matchedEmployee?.status ?? null,
        },
      };
    });
  }, [employeeLookup, requests]);

  const activeRequestsByEmployee = useMemo(() => {
    const map = new Map();
    (requests ?? []).forEach((request) => {
      if (!request || !isActiveAbsenceStatus(request?.status)) {
        return;
      }

      const candidateEmployeeIds = [
        request?.employeeId,
        request?.employee?.id,
        request?.employee?._id,
        request?.employee?.employeeId,
        request?.employee?.raw?.id,
        request?.employee?.raw?._id,
        request?.employee?.raw?.employeeId,
        request?.raw?.employeeId,
        request?.raw?.employee?.id,
        request?.raw?.employee?._id,
        request?.raw?.employee?.employeeId,
      ]
        .map((value) => (value !== undefined && value !== null ? String(value) : ''))
        .filter((value) => value.length > 0);

      const employeeKey = candidateEmployeeIds[0];
      if (!employeeKey) {
        return;
      }

      if (!map.has(employeeKey)) {
        map.set(employeeKey, []);
      }
      map.get(employeeKey).push(request);
    });
    return map;
  }, [requests]);

  const personalRequests = useMemo(() => {
    if (!isEmployeeView || !normalizedUserEmail) return [];
    return resolvedRequests.filter(
      (request) =>
        request?.employee?.accountEmail?.toLowerCase?.() === normalizedUserEmail
    );
  }, [isEmployeeView, normalizedUserEmail, resolvedRequests]);

  const currentUserEmployeeRecord = useMemo(() => {
    if (!user?.sub || !Array.isArray(employees)) {
      return null;
    }

    return (
      employees.find((employee) => {
        const rawUser = employee?.raw?.user;
        if (!rawUser) {
          return false;
        }
        if (typeof rawUser === 'string') {
          return rawUser === user.sub;
        }
        if (typeof rawUser === 'object') {
          const userId = rawUser?.id ?? rawUser?._id;
          return userId === user.sub;
        }
        return false;
      }) ?? null
    );
  }, [employees, user?.sub]);

  const employeeRecordFromEmployees = useMemo(() => {
    if (!isEmployeeView) {
      return null;
    }

    if (currentUserEmployeeRecord) {
      return currentUserEmployeeRecord;
    }

    if (Array.isArray(employees) && employees.length > 0) {
      return employees[0];
    }

    return null;
  }, [currentUserEmployeeRecord, employees, isEmployeeView]);

  const employeeProfile = useMemo(() => {
    if (!isEmployeeView) {
      return null;
    }

    const resolveAvatarDetails = (record) => {
      if (!record) {
        return { fileId: null, url: null };
      }

      const rawRecord = record?.raw ?? {};
      const rawUser = rawRecord?.user ?? {};
      const profileRecord = record?.profile ?? {};
      const employeeRecord = record?.employee ?? {};

      const avatarFileId = record?.avatarFileId
        ?? rawRecord?.avatarFileId
        ?? rawUser?.avatarFileId
        ?? profileRecord?.avatarFileId
        ?? employeeRecord?.avatarFileId
        ?? null;

      const avatarUrl = record?.avatarUrl
        ?? record?.avatar
        ?? rawRecord?.avatarUrl
        ?? rawRecord?.avatar
        ?? rawUser?.avatarUrl
        ?? rawUser?.avatar
        ?? profileRecord?.avatarUrl
        ?? profileRecord?.avatar
        ?? employeeRecord?.avatarUrl
        ?? employeeRecord?.avatar
        ?? null;

      return { fileId: avatarFileId ?? null, url: avatarUrl ?? null };
    };

    if (employeeRecordFromEmployees) {
      const { fileId, url } = resolveAvatarDetails(employeeRecordFromEmployees);

      return {
        id: employeeRecordFromEmployees.id,
        name: employeeRecordFromEmployees.name,
        position: employeeRecordFromEmployees.role,
        department: employeeRecordFromEmployees.department,
        email: employeeRecordFromEmployees.email,
        accountEmail: employeeRecordFromEmployees.email?.toLowerCase?.(),
        avatarFileId: fileId,
        avatarUrl: url,
        location:
          employeeRecordFromEmployees.location
          || translate('defaults.locationFallback', 'Primary Location'),
      };
    }

    if (personalRequests?.length > 0) {
      const baseEmployee = personalRequests[0]?.employee ?? null;
      if (!baseEmployee) {
        return null;
      }

      const { fileId, url } = resolveAvatarDetails(baseEmployee);

      return {
        ...baseEmployee,
        avatarFileId: fileId,
        avatarUrl: url,
      };
    }

    if (!user) {
      return null;
    }

    const { fileId, url } = resolveAvatarDetails(user);

    return {
      name: user?.fullName || user?.email,
      position: translate('defaults.positionFallback', 'Team Member'),
      department: translate('defaults.departmentFallback', 'Service'),
      email: user?.email,
      accountEmail: normalizedUserEmail,
      avatarFileId: fileId,
      avatarUrl: url,
      location:
        employees?.[0]?.location
        || translate('defaults.locationFallback', 'Primary Location'),
    };
  }, [employeeRecordFromEmployees, employees, isEmployeeView, normalizedUserEmail, personalRequests, user]);

  const employeeIdForRequest = useMemo(() => {
    if (!isEmployeeView) {
      return null;
    }
    if (employeeRecordFromEmployees?.id) {
      return employeeRecordFromEmployees.id;
    }
    if (employeeProfile?.id) {
      return employeeProfile.id;
    }
    return null;
  }, [employeeProfile, employeeRecordFromEmployees, isEmployeeView]);

  const canSubmitRequest = Boolean(employeeIdForRequest);

  const employeeRecordForModal = useMemo(() => {
    if (!isEmployeeView) {
      return null;
    }
    return employeeRecordFromEmployees ?? employeeProfile ?? null;
  }, [employeeProfile, employeeRecordFromEmployees, isEmployeeView]);

  const employeeNotifications = useMemo(() => {
    if (loading) {
      return { messages: null, timeOff: null };
    }

    return {
      messages: 0,
      timeOff: personalRequests.filter((request) => request.status === 'pending').length,
    };
  }, [loading, personalRequests]);

  const managerNotifications = useMemo(() => {
    if (loading) {
      return { messages: null, timeOff: null };
    }

    return {
      messages: 0,
      timeOff: resolvedRequests.filter((request) => request?.status === 'pending').length,
    };
  }, [loading, resolvedRequests]);

  const filteredRequests = useMemo(() => {
    const searchValue = filters?.search?.toLowerCase?.() ?? '';
    const hasDateRange = Boolean(filters?.startDate) && Boolean(filters?.endDate);
    const filterStart = hasDateRange ? new Date(filters.startDate) : null;
    const filterEnd = hasDateRange ? new Date(filters.endDate) : null;

    return (resolvedRequests ?? []).filter((request) => {
      const employeeName = request?.employee?.name?.toLowerCase?.() ?? '';
      const employeePosition = request?.employee?.position?.toLowerCase?.() ?? '';

      const matchesSearch =
        !searchValue ||
        employeeName.includes(searchValue) ||
        employeePosition.includes(searchValue);

      let matchesDateRange = true;
      if (hasDateRange && filterStart && filterEnd) {
        const requestStart = new Date(request.startDate);
        matchesDateRange = requestStart >= filterStart && requestStart <= filterEnd;
      }

      return (
        matchesSearch &&
        matchesDateRange
      );
    });
  }, [filters, resolvedRequests]);

  const requestCounts = useMemo(
    () => ({
      total: filteredRequests.length,
      pending: filteredRequests.filter((r) => r?.status === 'pending').length,
      approved: filteredRequests.filter((r) => r?.status === 'approved').length,
      denied: filteredRequests.filter((r) => r?.status === 'denied').length,
    }),
    [filteredRequests],
  );

  const [collapsedStatuses, setCollapsedStatuses] = useState({ denied: true });

  const groupedRequests = useMemo(() => {
    if (!Array.isArray(filteredRequests) || filteredRequests.length === 0) {
      return [];
    }

    const buckets = new Map();
    filteredRequests.forEach((request) => {
      const statusKey = typeof request?.status === 'string' ? request.status.toLowerCase() : 'unknown';
      if (!buckets.has(statusKey)) {
        buckets.set(statusKey, []);
      }
      buckets.get(statusKey).push(request);
    });

    const orderedStatuses = ['pending', 'approved', 'denied'];

    const result = [];
    orderedStatuses.forEach((status) => {
      if (buckets.has(status)) {
        result.push({
          status,
          requests: buckets.get(status),
        });
        buckets.delete(status);
      }
    });

    buckets.forEach((requests, status) => {
      result.push({ status, requests });
    });

    return result;
  }, [filteredRequests]);

  const resolveStatusHeading = useCallback(
    (status) => {
      const fallback =
        status && typeof status === 'string'
          ? status.charAt(0).toUpperCase() + status.slice(1)
          : 'Other';
      if (!status) {
        return translate('statuses.other', 'Other');
      }
      const normalized = status.toLowerCase();
      return translate(`statuses.${normalized}`, fallback);
    },
    [translate],
  );

  const handleToggleGroup = useCallback((status) => {
    if (!status) {
      return;
    }
    setCollapsedStatuses((previous) => {
      const currentValue = Boolean(previous?.[status]);
      return {
        ...previous,
        [status]: !currentValue,
      };
    });
  }, []);

  const handleApproveRequest = useCallback(
    async (requestId, comment = '') => {
      if (!requestId) {
        return;
      }
      try {
        await updateTimeOffStatus(requestId, {
          status: 'approved',
          ...(comment ? { managerResponse: comment } : {}),
        });
        await reloadData();
      } catch (error) {
        console.error(`Failed to approve absence request ${requestId}`, error);
        throw error;
      }
    },
    [reloadData],
  );

  const handleDenyRequest = useCallback(
    async (requestId, comment = '') => {
      if (!requestId) {
        return;
      }
      try {
        await updateTimeOffStatus(requestId, {
          status: 'denied',
          ...(comment ? { managerResponse: comment } : {}),
        });
        await reloadData();
      } catch (error) {
        console.error(`Failed to deny absence request ${requestId}`, error);
        throw error;
      }
    },
    [reloadData],
  );

  const handleSubmitRequest = useCallback(
    async (formValues) => {
      const targetEmployeeId = isEmployeeView
        ? employeeIdForRequest
        : formValues?.employeeId;

      if (!targetEmployeeId) {
        throw new Error(
          isEmployeeView
            ? 'Unable to determine employee profile for this request.'
            : 'Please choose an employee for this absence.',
        );
      }

      const toIsoDate = (value) => {
        if (!value) {
          return null;
        }
        const parts = value.split('-').map((segment) => Number.parseInt(segment, 10));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
          return null;
        }
        const [year, month, day] = parts;
        return new Date(Date.UTC(year, month - 1, day)).toISOString();
      };

      const startIso = toIsoDate(formValues.startDate);
      const endIso = toIsoDate(formValues.endDate);
      const reasonInput = formValues.reason?.trim() ?? '';
      const requiresReason =
        formValues.type === 'emergency' || formValues.type === 'personal';

      if (!startIso || !endIso) {
        throw new Error(translate('errors.invalidDates', 'Please provide valid start and end dates.'));
      }

      const requestedRange = computeAbsenceRange(
        formValues.startDate,
        formValues.endDate,
        formValues.dayPart ?? 'full-day',
      );

      if (!requestedRange) {
        throw new Error(translate('errors.invalidDates', 'Please provide valid start and end dates.'));
      }

      const normalizedEmployeeId = String(targetEmployeeId);
      const overlappingAbsence = (activeRequestsByEmployee.get(normalizedEmployeeId) ?? []).find(
        (existingRequest) => {
          if (!existingRequest?.startDate || !existingRequest?.endDate) {
            return false;
          }

          const existingRange = computeAbsenceRange(
            existingRequest.startDate,
            existingRequest.endDate,
            existingRequest.dayPart ?? 'full-day',
          );

          return rangesOverlap(requestedRange, existingRange);
        },
      );

      if (overlappingAbsence) {
        throw new Error(
          isEmployeeView
            ? translate(
                'errors.employeeOverlap',
                'You already have an absence scheduled during this time. Please choose different dates.',
              )
            : translate(
                'errors.managerOverlap',
                'This employee already has an absence scheduled during this time. Please choose different dates.',
              ),
        );
      }

      if (requiresReason && reasonInput.length === 0) {
        throw new Error(
          translate('errors.reasonRequired', 'Please include a brief reason for your request.'),
        );
      }

      const payload = {
        employeeId: String(targetEmployeeId),
        type: formValues.type,
        startDate: startIso,
        endDate: endIso,
        reason:
          reasonInput.length > 0
            ? reasonInput
            : formValues.type === 'emergency'
              ? translate('defaults.emergencyReason', 'Emergency absence request')
              : '',
      };

      payload.dayPart = formValues.dayPart ?? 'full-day';

      if (
        typeof formValues.duration === 'number'
        && Number.isFinite(formValues.duration)
        && formValues.duration > 0
      ) {
        payload.duration = formValues.duration;
      }

      setIsSubmittingRequest(true);
      try {
        await createTimeOffRequest(payload);
        await reloadData();
      } catch (error) {
        console.error('Failed to submit absence request', error);
        throw error;
      } finally {
        setIsSubmittingRequest(false);
      }
    },
    [activeRequestsByEmployee, employeeIdForRequest, isEmployeeView, reloadData],
  );

  const headerNotifications = isEmployeeView ? employeeNotifications : managerNotifications;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={employeeProfile?.location || null}
          onLocationChange={() => {}}
          notifications={headerNotifications}
        />
        <div className="pt-16">
          <LoadingState
            message={translate('page.loading', 'Loading absence data…')}
            heightClassName="min-h-[calc(100vh-4rem)]"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={employeeProfile?.location || null}
          onLocationChange={() => {}}
          notifications={headerNotifications}
        />
        <div className="pt-16 flex flex-col items-center justify-center text-muted-foreground space-y-4">
          <p>{translate('page.error.message', 'Unable to load absence data.')}</p>
          <Button variant="outline" onClick={reloadData}>
            {translate('page.error.retry', 'Try again')}
          </Button>
        </div>
      </div>
    );
  }

  if (isEmployeeView) {
    return (
      <>
        <div className="min-h-screen bg-background">
          <Header
            userRole={userRole}
            selectedLocation={
              employeeProfile?.location
              || translate('defaults.locationFallback', 'Downtown Location')
            }
            onLocationChange={() => {}}
            notifications={headerNotifications}
          />

          <div className="pt-16">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    {translate('page.employee.title', 'My Absences')}
                  </h1>
                  <p className="text-muted-foreground mt-2">
                    {translate(
                      'page.employee.subtitle',
                      'Review the status of your requests and plan upcoming time away from work.',
                    )}
                  </p>
                </div>
                <Button
                  variant="default"
                  iconName="Plus"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  {translate('page.employee.requestButton', 'Request Absence')}
                </Button>
              </div>

              <EmployeeRequestsView
                employee={employeeProfile}
                requests={personalRequests}
                onRequestTimeOff={() => setIsCreateModalOpen(true)}
              />
            </div>
          </div>
        </div>

        <CreateRequestModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleSubmitRequest}
          employee={employeeRecordForModal}
          isSubmitting={isSubmittingRequest}
          canSubmit={canSubmitRequest}
        />
      </>
    );
  }

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setIsModalOpen(true);
  };

  const handleDateSelect = (date) => {
    console.log('Selected date:', date);
  };

  return (
    <>
      <div className="min-h-screen bg-background">
        <Header
          userRole={userRole}
          selectedLocation={null}
          onLocationChange={() => {}}
          notifications={headerNotifications}
        />

        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col gap-6 mb-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">
                {translate('page.manager.title', 'Absence Management')}
              </h1>
              <p className="text-muted-foreground mt-2">
                {translate(
                  'page.manager.subtitle',
                  'Review and manage employee absence requests and maintain operational coverage',
                )}
              </p>
            </div>
            
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-start sm:gap-3 lg:justify-end">
              <div className="inline-flex overflow-hidden rounded-md border border-primary bg-background w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => setActiveView('requests')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    'border-r border-primary',
                    activeView === 'requests'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/40',
                  )}
                  aria-pressed={activeView === 'requests'}
                >
                  <Icon name="List" size={16} className="text-current" />
                  {translate('page.manager.actions.requests', 'Requests')}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveView('calendar')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                    activeView === 'calendar'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted/40',
                  )}
                  aria-pressed={activeView === 'calendar'}
                >
                  <Icon name="Calendar" size={16} className="text-current" />
                  {translate('page.manager.actions.calendar', 'Calendar')}
                </button>
              </div>
              <div className="w-full sm:w-auto">
                <Button
                  className="w-full sm:w-auto"
                  variant="default"
                  iconName="Plus"
                  onClick={() => setIsCreateModalOpen(true)}
                >
                  {translate('page.manager.actions.add', 'Add Absence')}
                </Button>
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          {activeView === 'requests' && (
            <FilterControls
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              requestCounts={requestCounts}
            />
          )}

          {/* Main Content */}
          {activeView === 'requests' && (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30 border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Employee</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Type</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Dates</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Duration</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRequests?.map(({ status, requests }) => {
                      const isCollapsible = status === 'denied';
                      const isCollapsed = Boolean(collapsedStatuses?.[status]);
                      const headingLabel = resolveStatusHeading(status);
                      return (
                        <React.Fragment key={status || 'unknown'}>
                          <tr className="bg-muted/20">
                            <td colSpan={6} className="px-6 py-3 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                              <div className="flex items-center justify-between">
                                <span>{headingLabel} ({requests.length})</span>
                                {isCollapsible && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleGroup(status)}
                                    className="flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <Icon
                                      name={isCollapsed ? 'ChevronRight' : 'ChevronDown'}
                                      size={14}
                                      className="mr-1"
                                    />
                                    {isCollapsed
                                      ? translate('page.groups.expand', 'Expand')
                                      : translate('page.groups.collapse', 'Collapse')}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {!isCollapsible || !isCollapsed ? (
                            requests.map((request) => (
                              <RequestCard
                                key={request?.id}
                                request={request}
                                onApprove={handleApproveRequest}
                                onDeny={handleDenyRequest}
                                onViewDetails={handleViewDetails}
                              />
                            ))
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden p-4 space-y-4">
                {groupedRequests?.map(({ status, requests }) => {
                  const isCollapsible = status === 'denied';
                  const isCollapsed = Boolean(collapsedStatuses?.[status]);
                  const headingLabel = resolveStatusHeading(status);
                  return (
                    <div key={status || 'unknown'} className="space-y-3">
                      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <span>{headingLabel}</span>
                        <div className="flex items-center space-x-2">
                          <span>{requests.length}</span>
                          {isCollapsible && (
                            <button
                              type="button"
                              onClick={() => handleToggleGroup(status)}
                              className="flex items-center text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Icon
                                name={isCollapsed ? 'ChevronRight' : 'ChevronDown'}
                                size={14}
                                className="mr-1"
                              />
                              {isCollapsed
                                ? translate('page.groups.expand', 'Expand')
                                : translate('page.groups.collapse', 'Collapse')}
                            </button>
                          )}
                        </div>
                      </div>
                      {!isCollapsible || !isCollapsed ? (
                        <div className="space-y-3">
                          {requests.map((request) => (
                            <RequestCard
                              key={request?.id}
                              request={request}
                              onApprove={handleApproveRequest}
                              onDeny={handleDenyRequest}
                              onViewDetails={handleViewDetails}
                              isCompact={true}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              {filteredRequests?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {translate('page.empty.title', 'No requests found')}
                  </h3>
                  <p className="text-muted-foreground">
                    {Object.values(filters)?.some((f) => f && f !== 'all')
                      ? translate('page.empty.filtered', 'Try adjusting your filters to see more results.')
                      : translate('page.empty.none', 'No absence requests have been submitted yet.')}
                  </p>
                </div>
              )}
            </div>
          )}

          {activeView === 'calendar' && (
            <CalendarView
              timeOffData={resolvedRequests}
              onDateSelect={handleDateSelect}
              locale={locale}
              translate={translate}
            />
          )}
          </div>
        </div>
      </div>
      {/* Request Details Modal */}
      <RequestModal
        request={selectedRequest}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedRequest(null);
        }}
        onApprove={handleApproveRequest}
        onDeny={handleDenyRequest}
      />
      <ManagerCreateAbsenceModal
        isOpen={isCreateModalOpen && !isEmployeeView}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleSubmitRequest}
        employees={employees}
        currentUserEmployee={currentUserEmployeeRecord}
        isSubmitting={isSubmittingRequest}
      />
    </>
  );

};

export default TimeOffManagement;
